import * as React from 'react'
import Media from 'react-media'

import {
  ProductBlockProps,
  WishlistButton,
} from '@thg-commerce/enterprise-components'
import { WishlistButtonType } from '@thg-commerce/enterprise-components/Product/src/WishlistButton/WishlistButton'
import {
  i18n,
  useFormattableI18nProperty,
  useSiteConfig,
} from '@thg-commerce/enterprise-core'
import { PRODUCT_CONTENT_KEY } from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Query/Product'
import { WishlistButtonStyleInterface } from '@thg-commerce/enterprise-pages/src/Product/theme'
import { Button } from '@thg-commerce/gravity-elements'
import { CarouselButtonPlacement } from '@thg-commerce/gravity-patterns'
import { GridItem, Picture, PictureProps } from '@thg-commerce/gravity-system'
import { mq } from '@thg-commerce/gravity-theme'
import { HorizontalAlignment } from '@thg-commerce/gravity-theme/alignments'
import { Padding } from '@thg-commerce/gravity-theme/padding'

import {
  Description,
  ProductBlockGrid,
  StyledButtonGrid,
  StyledCarousel,
  StyledCarouselGrid,
  StyledGrid,
  StyledGridItemContainer,
  StyledImage,
  StyledProductBlock,
  StyledWishlistContainer,
  Title,
} from './styles'

export enum ImageOrientation {
  LANDSCAPE = 'landscape',
  PORTRAIT = 'portrait',
}

enum ImageAspectRatio {
  PORTRAIT = '5/7',
  LANDSCAPE = '16/9',
}

interface GridItemInterface {
  colStart: number[] | number
  colSpan: number[] | number
  rowStart: number[] | number
  rowSpan: number[] | number
}

export interface ShopTheRoomProps {
  title: string
  description: string
  orientation: ImageOrientation
  image: {
    urls: {
      small: string
      medium: string
      large: string
    }
    alt?: string
  }
  picture?: PictureProps
  productBlocks: ProductBlockProps[]
  displayQuickBuyButton?: boolean
  widgetIndex: number
  shopAll: {
    link: string
    label: string
  }
  componentsGrid: {
    title: GridItemInterface
    image: GridItemInterface
    carousel: GridItemInterface
    button: GridItemInterface
  }
  displayAsGrid: boolean
  indicatorPadding: Padding
  wishListButtonStyle?: WishlistButtonStyleInterface
  wishlist?: {
    itemCount: number
    skus: number[]
  }
  breakpoints: {
    lg: number
    md: number
    sm: number
    xs: number
  }
}

export const ShopTheRoom = (props: ShopTheRoomProps) => {
  const {
    hideProductListImageRollover,
    hideProductListProductBlockButton,
  } = useSiteConfig()

  const i18nText = {
    carouselText: {
      leftScrollLabel: i18n('carousel.controls.left.arialabel'),
      rightScrollLabel: i18n('carousel.controls.right.arialabel'),
    },
    wishlist: {
      addToWishlistTextDynamic: useFormattableI18nProperty(
        'product.addtowishlistbutton.addtowishlist.text.dynamic',
      ),
      addToWishlistText: i18n('product.addtowishlistbutton.addtowishlist.text'),
      savedToWishlistText: i18n(
        'product.addtowishlistbutton.savedtowishlist.text',
      ),
      wishlistTooltip: {
        closeButtonText: i18n(
          'product.addtowishlist.tooltip.button.close.text',
        ),
      },
      wishlistTooltipContent: {
        loginSignupText: i18n('product.addtowishlist.tooltip.loginsignup.text'),
        toUseWishlistText: i18n('product.addtowishlist.tooltip.text'),
      },
    },
  }

  const productBlocks = props.productBlocks
    .filter((product) => product.sku)
    .map((product, index) => {
      const inWishlist = props.wishlist?.skus.includes(
        parseInt(product.sku, 10),
      )
      return (
        <React.Fragment>
          <StyledProductBlock
            key={index}
            {...product}
            displayQuickBuyButton={props.displayQuickBuyButton || false}
            brand={{
              name: product.brand?.name || '',
              imageUrl: product.brand?.imageUrl,
            }}
            image={{
              ...product.image,
              lazy: props.widgetIndex > 1 ? true : product.image.lazy,
            }}
            {...(product.picture?.sources && {
              picture: {
                ...product.picture,
                lazy: props.widgetIndex > 1 ? true : product.image.lazy,
              },
            })}
            {...(!hideProductListImageRollover &&
              product.hoverImage && {
                hoverImage: {
                  ...product.hoverImage,
                  lazy: props.widgetIndex > 1 ? true : product.hoverImage.lazy,
                },
              })}
            enableQuickbuyButtonOnDesktop={true}
            hasButton={
              (!hideProductListProductBlockButton &&
                props.displayQuickBuyButton) ??
              false
            }
          />
          <StyledWishlistContainer>
            <WishlistButton
              buttonType={WishlistButtonType.HEART}
              sku={parseInt(product.sku, 10)}
              inWishlist={inWishlist}
              i18nText={i18nText.wishlist}
              buttonStyle={props.wishListButtonStyle}
              title={product.title.value}
              price={product.price?.price?.defaultPrice}
              externalIdentifier={product?.externalIdentifier}
              brandName={product?.brand?.name}
              itemCategories={product?.content?.find(
                (item) => item.key === PRODUCT_CONTENT_KEY.MAT_CATEGORY,
              )}
            />
          </StyledWishlistContainer>
        </React.Fragment>
      )
    })

  const carouselBreakpoints = {
    2: [2, 2, 2, 2],
    3: [2, 3, 2, 2],
    default:
      props.orientation === ImageOrientation.PORTRAIT
        ? [2, 3, 2, 2]
        : [2, 3, 4, 4],
  }

  return (
    <StyledGrid columns={12} rows={9}>
      <StyledGridItemContainer
        colStart={props.componentsGrid.title.colStart}
        colSpan={props.componentsGrid.title.colSpan}
        rowStart={props.componentsGrid.title.rowStart}
        rowSpan={props.componentsGrid.title.rowSpan}
      >
        <Title
          textAlign={
            props.orientation === ImageOrientation.PORTRAIT &&
            !props.displayAsGrid
              ? HorizontalAlignment.LEFT
              : HorizontalAlignment.CENTER
          }
        >
          {props.title}
        </Title>
        <Description
          textAlign={
            props.orientation === ImageOrientation.PORTRAIT &&
            !props.displayAsGrid
              ? HorizontalAlignment.LEFT
              : HorizontalAlignment.CENTER
          }
        >
          {props.description}
        </Description>
      </StyledGridItemContainer>
      <GridItem
        colStart={props.componentsGrid.image.colStart}
        colSpan={props.componentsGrid.image.colSpan}
        rowStart={props.componentsGrid.image.rowStart}
        rowSpan={props.componentsGrid.image.rowSpan}
        style={{ display: 'flex', justifyContent: 'center' }}
      >
        {props.picture ? (
          <Picture
            {...props.picture}
            aspectRatio={
              props.orientation === ImageOrientation.PORTRAIT
                ? ImageAspectRatio.PORTRAIT
                : ImageAspectRatio.LANDSCAPE
            }
            style={{
              width: '100%',
              objectFit: 'cover',
              maxWidth:
                props.orientation === ImageOrientation.PORTRAIT &&
                props.displayAsGrid
                  ? '538px'
                  : '100%',
            }}
          />
        ) : (
          <StyledImage
            src={[
              {
                url: props.image.urls.small,
                breakpoint: 'sm',
              },
              {
                url: props.image.urls.medium,
                breakpoint: 'md',
              },
              {
                url: props.image.urls.large,
                breakpoint: 'lg',
              },
            ]}
            width="100%"
            height="100%"
            alt={props.image.alt || props.title || ''}
            {...(props.orientation === ImageOrientation.PORTRAIT &&
              props.displayAsGrid && {
                maxWidth: '538px',
              })}
            aspectRatio={
              props.orientation === ImageOrientation.PORTRAIT
                ? ImageAspectRatio.PORTRAIT
                : ImageAspectRatio.LANDSCAPE
            }
          />
        )}
      </GridItem>

      <StyledCarouselGrid
        colStart={props.componentsGrid.carousel.colStart}
        colSpan={props.componentsGrid.carousel.colSpan}
        rowStart={props.componentsGrid.carousel.rowStart}
        rowSpan={props.componentsGrid.carousel.rowSpan}
        displayAsGrid={props.displayAsGrid}
      >
        {props.displayAsGrid ? (
          <ProductBlockGrid>
            {productBlocks.map((block) => (
              <div style={{ position: 'relative' }}>{block}</div>
            ))}
          </ProductBlockGrid>
        ) : (
          <Media query={mq(props.breakpoints, 'sm', true)}>
            {(isDesktop) => {
              return (
                <StyledCarousel
                  items={productBlocks}
                  i18n={i18nText.carouselText}
                  itemsPerSlide={
                    carouselBreakpoints[productBlocks?.length] ||
                    carouselBreakpoints.default
                  }
                  hideControlsOnSingleSlide={true}
                  inactiveSlideFullOpacity={true}
                  buttonPlacement={CarouselButtonPlacement.BottomRight}
                  indicatorStyle="dot"
                  enableSlidePreview={true}
                  indicatorPadding={props.indicatorPadding}
                  dotHorizontalMargin={0.5}
                  enableWhiteControls={true}
                  {...(isDesktop && {
                    customTransform:
                      props.orientation === ImageOrientation.PORTRAIT
                        ? 102
                        : 106,
                  })}
                />
              )
            }}
          </Media>
        )}
      </StyledCarouselGrid>

      <StyledButtonGrid
        colStart={props.componentsGrid.button.colStart}
        colSpan={props.componentsGrid.button.colSpan}
        rowStart={props.componentsGrid.button.rowStart}
        rowSpan={props.componentsGrid.button.rowSpan}
        isPortrait={
          props.orientation === ImageOrientation.PORTRAIT &&
          !props.displayAsGrid
        }
      >
        <Button
          href={props.shopAll.link}
          ariaLabel={props.shopAll.label}
          renderedAs="a"
        >
          {props.shopAll.label}
        </Button>
      </StyledButtonGrid>
    </StyledGrid>
  )
}
